<script lang="ts" setup>
import type { InputProps } from '~kui/types/input';
import type { StringOptions } from '~kui/types/selectOption';

import { useVuelidate } from '@vuelidate/core';
import { helpers, numeric } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

interface ClassYearOption {
  value: number | null | undefined;
  label: string;
}

interface Props extends Omit<InputProps, 'size'> {
  placeholder?: string;
  errorMessages?: string[];
  hideLabel?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  dataTestId?: string;
  clearable?: boolean;
  searchable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  classYearOptions?: ClassYearOption[];
  graduatedLabel?: string;
  skipValidation?: boolean;
  required?: boolean;
  requiredMessage?: string;
  version?: 'v1' | 'v2';
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: undefined,
  errorMessages: () => [],
  disabled: false,
  readonly: false,
  dataTestId: 'kui-school-class-year-select',
  clearable: false,
  searchable: false,
  hideLabel: false,
  hideErrorMessage: false,
  size: 'md',
  classYearOptions: () => [],
  graduatedLabel: 'Graduated',
  required: false,
  requiredMessage: undefined,
  version: 'v1',
});

const { t } = useI18n();

const value = defineModel<number | null | undefined>({ default: undefined });
const internalValue = computed({
  get: () => {
    if (value.value === null) {
      return 'graduated';
    }
    if (value.value === undefined) {
      return null;
    }
    return value.value.toString();
  },
  set: (newValue: string | string[] | null) => {
    if (newValue === null || newValue === undefined) {
      value.value = undefined;
    }
    else if (newValue === 'graduated') {
      value.value = null;
    }
    else if (typeof newValue === 'string') {
      const numValue = Number.parseInt(newValue, 10);
      value.value = Number.isNaN(numValue) ? undefined : numValue;
    }
  },
});

// A select that can't be cleared needs a grade or "Graduated" before its form saves.
const notUndefinedRule = helpers.withMessage(
  () => props.requiredMessage ?? t('kui.classYearSelect.required'),
  (value: unknown) => value !== undefined,
);

const validation = useVuelidate(
  {
    classYear: {
      numeric,
      ...(props.clearable === false ? { notUndefinedRule } : undefined),
    },
  },
  { classYear: value },
);

const combinedErrorMessages = computed(() => {
  const errors = validation.value.$errors.map(e => unref(e.$message));
  return [...props.errorMessages, ...errors];
});

const options = computed<StringOptions[]>(() => {
  const yearOptions = props.classYearOptions.map(opt => ({
    value: opt.value?.toString() || '',
    label: opt.label,
  }));
  return [...yearOptions, { value: 'graduated', label: props.graduatedLabel }];
});
</script>

<template>
  <KUICoreInputCombobox
    v-model="internalValue"
    :as="searchable ? 'search' : 'select'"
    :options="options"
    :placeholder="placeholder"
    :label="label"
    :hide-label="hideLabel"
    :disabled="disabled"
    :required="required"
    :clearable="clearable"
    :error-messages="combinedErrorMessages"
    :data-test-id="dataTestId"
    :has-search="searchable"
    :version="version"
  />
</template>
